import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import ShopCard from "../component/ShopCard";
import FruitShopCard from "../component/FruitShopCard";
import "../component/css/ShopCard.css";
import "./Cart.css";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "https://yaasaai.com/myphpapi"; // Your API base
const IMAGE_BASE_URL = "https://yaasaai.com/vegetableimages/";

const Cart = ({ phoneNumber, userEmail, onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popupMessage, setPopupMessage] = React.useState("");
  const [showPopup, setShowPopup] = React.useState(false);

  const navigate = useNavigate();

  // Fetch cart items
  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/getcartdata.php?phoneNumber=${encodeURIComponent(
            userEmail
          )}`
        );
        if (!response.ok) {
          if (response.status === 404) {
            setCartItems([]);
            return;
          }
          throw new Error(
            `Server Error: ${response.status} - ${response.statusText}`
          );
        }
        const data = await response.json();
        setCartItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userEmail) fetchCartItems();
  }, [userEmail]);

  //tracking script
  window.gtag("event", "cart_visited", {
    screen_name: "cart page visited",
    phone: userEmail,
  });

  // Delete item from cart by id
  const deleteItem = async (id) => {
    try {
      // ✅ First: find the item to be deleted before updating state
      const deletedItem = cartItems.find((item) => item.id === id);

      const response = await fetch(`${API_BASE_URL}/removeitem.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, phoneNumber: userEmail }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete item");

      // ✅ Send GA4 event (only if deletedItem was found)
      if (typeof window.gtag === "function" && deletedItem) {
        window.gtag("event", "remove_from_cart", {
          veg_name: deletedItem.veg_name,
          veg_id: deletedItem.id,
          quantity: deletedItem.veg_quantity,
          price: deletedItem.veg_rate,
          shop_id: deletedItem.shop_id,
          user_id: userEmail,
        });
      }

      // ✅ Update state
      setCartItems((prevCart) => prevCart.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  // Group items by shop
  const groupItemsByShop = (items) => {
    if (!Array.isArray(items)) return {};
    return items.reduce((acc, item) => {
      const shop = item.shop_name || "Other Shop";
      if (!acc[shop]) acc[shop] = [];
      acc[shop].push(item);
      return acc;
    }, {});
  };

  if (loading) return <div className="container">Loading cart items...</div>;
  if (error) return <div className="container text-danger">Error: {error}</div>;

  const groupedItems = groupItemsByShop(cartItems);

  // Calculate overall total
  const overallTotal = cartItems.reduce(
    (sum, item) => sum + (parseFloat(item.veg_rate) || 0),
    0
  );

  // Checkout function with minimum ₹50 per shop check
  const handleCheckout = () => {
    const shopsBelowMinimum = Object.entries(groupedItems)
      .filter(([_, items]) => {
        const shopTotal = items.reduce(
          (sum, item) => sum + (parseFloat(item.veg_rate) || 0),
          0
        );
        return shopTotal < 50;
      })
      .map(([shopName]) => shopName);

    if (shopsBelowMinimum.length > 0) {
      setPopupMessage(
        `ऑर्डर पूरा करने के लिए इन दुकानों से ₹50 या ज़्यादा का सामान जोड़ें: ${shopsBelowMinimum.join(
          ", "
        )}`
      );
      setShowPopup(true);
      return;
    }

    // ✅ GA4 tracking: Checkout Started
    if (typeof window.gtag === "function") {
      window.gtag("event", "checkout_started", {
        user_id: userEmail,
        total_cart_amount: overallTotal.toFixed(2),
        shop_count: Object.keys(groupedItems).length,
      });
    }

    navigate("/checkoutform", {
      state: { finaltotal: overallTotal.toFixed(2), userEmail },
    });
  };

  return (
    <div className="container margin_top_cont">
      <h6 className="billing-header text-center">
        Details of Purchase Vegetables
      </h6>
      {cartItems.length === 0 ? (
        <div className="alert alert-warning">
          Your cart is empty. <a href="/">Shop Now</a>
        </div>
      ) : (
        <>
          {Object.entries(groupedItems).map(([shopName, items]) => {
            const shopTotal = items.reduce(
              (sum, item) => sum + (parseFloat(item.veg_rate) || 0),
              0
            );
            return (
              <div key={shopName} className="shop-group mb-4">
                <h6 className="shop-header">{shopName}</h6>
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="column cart_view_mb align-items-center mb-0"
                  >
                    <div className="col-2 text-center">
                      <img
                        src={`${IMAGE_BASE_URL}${item.veg_image_name}`}
                        alt={item.veg_name}
                        className="cart-item-img"
                        title={item.veg_name}
                      />
                    </div>
                    <div className="col-4 text-left veg_name">
                      {item.veg_name}
                    </div>
                    <div className="col-3 text-center veg_name">
                      {item.veg_quantity}
                    </div>
                    <div className="col-2 text-center veg_name">
                      ₹{parseFloat(item.veg_rate) || 0}
                    </div>
                    <div className="col-1 text-center">
                      <button
                        className="btn btn-sm btn-danger rounded-pill px-1 shadow-sm"
                        onClick={() => deleteItem(item.id)}
                        title="remove veg"
                        style={{
                          fontWeight: "bold",
                          fontSize: "11px",
                          letterSpacing: "0.5px",
                          boxShadow: "0 2px 5px rgba(236, 226, 222, 0.97)",
                          color: "transparent",
                          textShadow:
                            "0 0 1px white, 0 0 1px white, 0 0 1px white, 0 0 2px white",
                        }}
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                ))}
                {/* Uncomment if you want shop total */}
                {/* <div className="text-right pr-3 font-weight-bold">
                  Shop Name: {shopName} | Total: ₹{shopTotal.toFixed(2)}
                </div> */}
              </div>
            );
          })}
          {showPopup && (
            <>
              {/* Backdrop */}
              <div
                onClick={() => setShowPopup(false)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  zIndex: 9998,
                }}
              />

              {/* Popup Box */}
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "#ffcc00",
                  color: "#333",
                  padding: "25px 35px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  zIndex: 9999,
                  maxWidth: "90%",
                  textAlign: "center",
                  fontWeight: "600",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => setShowPopup(false)}
              >
                {popupMessage}
                <br />
                <small style={{ fontWeight: "400", fontSize: "0.85em" }}>
                  (Tap anywhere to close)
                </small>
              </div>
            </>
          )}

          <div className="row">
            <div className="col-sm-6 col-lg-6 col-md-6 col-12">
              <h6 className="billing-header text-center">Billing Details</h6>
              <table className="table table-striped table-grey table_price">
                <thead>
                  <tr>
                    <th>Shop Name</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedItems).map(([shopName, items]) => {
                    const shopTotal = items.reduce(
                      (sum, item) => sum + (parseFloat(item.veg_rate) || 0),
                      0
                    );
                    return (
                      <tr key={shopName} className="shop-total">
                        <td>{shopName}</td>
                        <td>₹ {shopTotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <strong>Overall Total</strong>
                    </td>
                    <td>
                      <strong>₹{overallTotal.toFixed(2)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="btn btn-success btn-lg btn-block"
                onClick={handleCheckout}
              >
                Checkout
              </button>
            </div>

            {/* <div className="col-sm-6 col-lg-6 col-md-6 col-12 d-flex justify-content-start">
              <div className="extra-info-block">
                <span className="extra-info-text">more links</span>
                <a href="">view offer</a>
                <a href="">view offer</a>
              </div>
            </div>*/}
          </div>

          <ShopCard />
          <FruitShopCard />
        </>
      )}
    </div>
  );
};

export default Cart;
